import { getUserPreferences } from 'core/service/user-preferences/user-preferences.service';
import { AppSettingsConfig, DEFAULT_APP_SETTINGS_CONFIG } from 'core/service/user-preferences/domain';
import { mapRowPerPageApiToModel } from 'core/service/user-preferences/domain/mappers/row-per-page.mapper';
import { DEFAULT_LANGUAGE } from 'core/constants/constants';
import { UserCustomFilter } from 'core/utils/types/user-custom-filter.type';
import { mapUserPreferencesViewConfigFromAPIToModel } from 'modules/global/mappers';
import { UserPreferencesViewConfig } from 'modules/global/model';
import { OperatorsFilterFields } from 'modules/operators/types/operators-filter.type';
import React, { useEffect, useState } from 'react';
import { useUserLanguageManager } from '../hooks/use-user-language-manager.hook';
import { useUserPageSizerManager } from '../hooks/use-user-page-sizer-manager.hook';
import { UserPreferencesContext } from '../user-preferences.context';

type UserPreferencesProviderProps = React.PropsWithChildren;

export const UserPreferencesProvider: React.FC<UserPreferencesProviderProps> = ({ children }) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [sideMenuEntries, setSideMenuEntries] = useState<UserCustomFilter<OperatorsFilterFields>[]>([]);
    const [userPreferencesId, setUserPreferencesId] = useState<string>('');
    const [sideMenuEntriesHasChanged, setSideMenuEntriesHasChanged] = useState<boolean>(false);

    const { userLanguage, applyUserLanguage, updateUserLanguage } = useUserLanguageManager({ userPreferencesId });
    const [userPreferencesViewConfig, setUserPreferencesViewConfig] = useState<UserPreferencesViewConfig | undefined>(
        undefined
    );
    const [appSettings, setAppSettings] = useState<AppSettingsConfig>(DEFAULT_APP_SETTINGS_CONFIG);
    const [operatorReportHeaderValue, setOperatorReportHeaderValue] = useState<string>('');
    const [fetchError, setFetchError] = useState<Error | null>(null);
    const { pageSize, isPaginationLoading, setPageSize, updatePageSize } = useUserPageSizerManager({
        userPreferencesId,
    });

    if (fetchError) {
        throw fetchError;
    }

    useEffect(() => {
        setIsLoading(true);
        const fetchUserPreferences = async () => {
            if (isLoading) {
                return;
            }

            try {
                const { userPreferences, appSettingsConfig } = await getUserPreferences();
                const {
                    userPreferencesId: userPreference,
                    filters,
                    rowsPerPage,
                    operatorReportHeader,
                } = userPreferences;
                const mappedUserPreferences = filters.map((filter) => {
                    const rawFrontendData: unknown = filter.frontendData;
                    const frontendData =
                        typeof rawFrontendData === 'string'
                            ? JSON.parse(rawFrontendData || '{}')
                            : (rawFrontendData ?? {});
                    return {
                        id: filter.filterId,
                        name: filter.filterName,
                        entity: filter.type,
                        fields: frontendData?.filter ? frontendData.filter : frontendData, // frontendData retrocompatibility handling
                        sorting: frontendData?.sorting ? frontendData.sorting : undefined,
                    };
                });
                const mappedUserPreferencesViewConfig = mapUserPreferencesViewConfigFromAPIToModel(userPreferences);
                await applyUserLanguage(userPreferences.language?.languageName || DEFAULT_LANGUAGE);
                setUserPreferencesViewConfig(mappedUserPreferencesViewConfig);
                setUserPreferencesId(userPreference);
                setSideMenuEntries(mappedUserPreferences);
                setPageSize(mapRowPerPageApiToModel(rowsPerPage));
                setOperatorReportHeaderValue(operatorReportHeader);
                setSideMenuEntriesHasChanged(false);
                setAppSettings(appSettingsConfig);
            } catch (error) {
                setFetchError(error instanceof Error ? error : new Error(String(error)));
            } finally {
                setIsLoading(false);
            }
        };

        fetchUserPreferences();
    }, [sideMenuEntriesHasChanged]);

    const contextValue = React.useMemo(
        () => ({
            userLanguage,
            userPreferencesViewConfig: userPreferencesViewConfig || ({} as UserPreferencesViewConfig),
            sideMenuEntries,
            pageSize,
            userPreferencesId,
            operatorReportHeaderValue,
            isLoading,
            isPaginationLoading,
            appSettings,
            setSideMenuEntries,
            setUserPreferencesViewConfig,
            setOperatorReportHeaderValue,
            refreshUserPreferences: () => {
                setSideMenuEntriesHasChanged(true);
            },
            updateUserLanguage,
            updatePageSize,
            setAppSettings,
        }),
        [
            userLanguage,
            userPreferencesViewConfig,
            sideMenuEntries,
            pageSize,
            userPreferencesId,
            operatorReportHeaderValue,
            isLoading,
            isPaginationLoading,
            appSettings,
            setSideMenuEntries,
            setUserPreferencesViewConfig,
            setOperatorReportHeaderValue,
            updateUserLanguage,
            updatePageSize,
            setAppSettings,
        ]
    );

    return <UserPreferencesContext.Provider value={contextValue}>{children}</UserPreferencesContext.Provider>;
};
